import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeObserver } from '../../../../common/utilities/resize-observer';
import { useSelector } from 'react-redux';
import { useSlicerTheme } from '../../slicer-widget/common/slicer-widget-consts.style';
import _, { get, head, last, noop } from 'lodash';
import moment from '../../../../common/Moment';
import { messages } from '../../../../i18n';
import { useTimestampSlicer } from '../timestamp-slicer.hook';
import { ITimeSlicerOption, TimestampSlicerTypes } from '../../interfaces';
import {
  defaultDate,
  nowOrSpecifiedDate,
  toIso8601,
} from '../../../../components/calendar/date-picker/date-picker.utils';
import { RangeValue } from '@react-types/shared';
import { ZonedDateTime } from '@internationalized/date';
import { ITimestampSlicerOptionsProps } from './timestamp-slicer-options.interfaces';

export const timestampOptionLabels = {
  [TimestampSlicerTypes.CURRENT_AND_PREV]: messages.slicer.setTimeFrame,
  [TimestampSlicerTypes.IN_PAST]: messages.slicer.inPast,
  [TimestampSlicerTypes.SINCE_PERIOD]: messages.slicer.since,
  [TimestampSlicerTypes.BETWEEN_PERIOD]: messages.slicer.between,
};

export const useTimestampSlicerOptionHooks = ({
  vizId,
  fieldName,
  optionType,
  breadcrumbState,
  selectTimeFrame,
}: {
  vizId: string;
  fieldName: string;
  optionType: TimestampSlicerTypes;
  breadcrumbState?: ITimestampSlicerOptionsProps['breadcrumbState'];
  selectTimeFrame: ITimestampSlicerOptionsProps['selectTimeFrame'];
}) => {
  const {
    pushBreadcrumb = noop,
    breadcrumbs = noop,
    clear = noop,
  } = breadcrumbState ?? {};
  const [_anchor, setAnchorRefEl] = useState<HTMLElement>(null);
  const anchorEl = useRef<HTMLElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const setAnchorEl = useCallback(
    (el: HTMLElement | null) => {
      // the use of the setAnchorRefEl and ref prevent flickering of the popper portal
      anchorEl.current = el;
      setAnchorRefEl(el);
    },
    [anchorEl],
  );
  const resizeElement = useMemo(
    () => anchorEl?.current ?? _anchor,
    [anchorEl, _anchor],
  );
  const { right: anchorRightPos = 0 } = useResizeObserver(resizeElement) ?? {};
  const isOpen = useMemo(() => Boolean(resizeElement), [resizeElement]);
  const isMobile = useSelector((state: any) => !!state.main.isMobile);

  const { isDashletMode, width } = useSlicerTheme();

  const disablePortal = isMobile || isDashletMode;

  const closeSubMenu = useCallback(() => {
    clear();
    setAnchorEl(null);
  }, [setAnchorEl, clear]);

  const optionLabel = get(timestampOptionLabels, optionType, '');

  const handleOnClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    pushBreadcrumb(optionLabel);

    isOpen && closeSubMenu();
    setAnchorEl(isOpen ? null : e.currentTarget);
  };

  useEffect(() => {
    if (isMobile && breadcrumbs.length === 0) {
      closeSubMenu();
    }
  }, [breadcrumbs, closeSubMenu, isMobile]);

  useEffect(() => {
    moment.updateLocale(moment.locale(), {
      invalidDate: '',
    });
    return () =>
      moment.updateLocale(moment.locale(), {
        invalidDate: messages.slicer.invalidDate,
      });
  }, []);

  const { slicerOption: { type, operands = [] } = {} } = useTimestampSlicer({
    vizId,
    name: fieldName,
  });

  const setSlicerSelection = useCallback(
    (_type: TimestampSlicerTypes, _operands: ITimeSlicerOption['operands']) =>
      selectTimeFrame({
        type: _type,
        operands: _operands,
      }),
    [selectTimeFrame],
  );

  const setCurrAndPrev = useCallback(
    (_operands: ITimeSlicerOption['operands']) =>
      setSlicerSelection(TimestampSlicerTypes.CURRENT_AND_PREV, _operands),
    [setSlicerSelection],
  );

  const setInPast = useCallback(
    (_operands: ITimeSlicerOption['operands']) =>
      setSlicerSelection(TimestampSlicerTypes.IN_PAST, _operands),
    [setSlicerSelection],
  );

  const setSinceTimeFrame = useCallback(
    (date: ZonedDateTime) => {
      if (_.isNil(date)) {
        clear();
      } else {
        setSlicerSelection(TimestampSlicerTypes.SINCE_PERIOD, [
          toIso8601(date),
        ]);
      }
    },
    [clear, setSlicerSelection],
  );

  const setBetweenTimeFrame = useCallback(
    (date: ZonedDateTime[]) => {
      if (_.isNil(date) || date.length < 2) {
        clear();
      } else {
        setSlicerSelection(TimestampSlicerTypes.BETWEEN_PERIOD, [
          toIso8601(date[0]),
          toIso8601(date[1], true),
        ]);
      }
    },
    [clear, setSlicerSelection],
  );

  const currPrevDate = useMemo(() => {
    if (type !== TimestampSlicerTypes.CURRENT_AND_PREV) {
      return [];
    }

    return operands;
  }, [operands, type]);

  const iPastDate = useMemo(() => {
    if (type !== TimestampSlicerTypes.IN_PAST) {
      return [];
    }

    return operands;
  }, [operands, type]);

  const sinceDate = useMemo(() => {
    if (type !== TimestampSlicerTypes.SINCE_PERIOD) {
      return defaultDate();
    }

    return nowOrSpecifiedDate(head(operands));
  }, [operands, type]);

  const betweenDates: RangeValue<ZonedDateTime> = useMemo(() => {
    if (type !== TimestampSlicerTypes.BETWEEN_PERIOD) {
      return {
        start: defaultDate(),
        end: defaultDate(),
      };
    }

    return {
      start: nowOrSpecifiedDate(head(operands)),
      end: nowOrSpecifiedDate(last(operands)),
    };
  }, [operands, type]);

  return {
    subMenuRef,
    anchorRightPos,
    width,
    disablePortal,
    handleOnClick,
    setCurrAndPrev,
    setInPast,
    setSinceTimeFrame,
    setBetweenTimeFrame,
    currPrevDate,
    iPastDate,
    sinceDate,
    betweenDates,
    isOpen,
    optionLabel,
    resizeElement,
    isMobile,
    closeSubMenu,
  };
};
